import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  County,
  CountyAddress,
  CountySelectionEvent,
  CountySelectionState,
  countySelectionReducer,
  emptyCountySelection,
} from './county-selection.machine';

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function readCountyAddress(control: AbstractControl): CountyAddress {
  return {
    zipCode: text(control.get('zipCode')?.value),
    countyName: text(control.get('countyName')?.value),
    fipsCode: text(control.get('fipsCode')?.value),
    state: text(control.get('state')?.value),
  };
}

export class CountySelectionController {
  private readonly selection = new BehaviorSubject<CountySelectionState>(emptyCountySelection);
  readonly state$ = this.selection.asObservable();
  private readonly requests = new Subject<{ zip: string; load: boolean } | null>();
  private readonly subscriptions = new Subscription();
  private readonly lookupValidator: ValidatorFn = () => this.lookupErrors(this.state.status);

  constructor(
    private readonly form: AbstractControl,
    private readonly zip: AbstractControl,
    private readonly county: AbstractControl,
    private readonly fips: AbstractControl,
    private readonly stateControl: AbstractControl | null,
    private readonly lookup: (zip: string) => Observable<County[]>,
    private readonly stateFormat: 'stateName' | 'label' = 'stateName',
  ) {
    this.zip.addValidators(this.lookupValidator);
    this.subscriptions.add(
      this.requests
        .pipe(
          switchMap(request =>
            request
              ? timer(request.load ? 0 : 500).pipe(
                  switchMap(() => this.lookup(request.zip)),
                  map(counties => ({ type: 'lookupSucceeded', zip: request.zip, counties }) as const),
                  catchError(() => [{ type: 'lookupFailed', zip: request.zip } as const]),
                )
              : EMPTY,
          ),
        )
        .subscribe(event => this.dispatch(event)),
    );
    this.subscriptions.add(
      this.zip.valueChanges.subscribe(value => this.dispatch({ type: 'zipEdited', zip: text(value) })),
    );
  }

  get state() {
    return this.selection.value;
  }
  get counties(): County[] {
    return 'counties' in this.state ? this.state.counties : [];
  }
  get selectedFips(): string {
    return this.state.status === 'selected' ? this.state.county.countyFIPS : '';
  }
  get loading(): boolean {
    return this.state.status === 'loading';
  }
  get error(): string | null {
    switch (this.state.status) {
      case 'invalidZip':
        return 'This zip code is invalid';
      case 'lookupFailed':
        return 'Unable to load counties. Please try again.';
      default:
        return null;
    }
  }

  load(address?: CountyAddress): void {
    this.dispatch({
      type: 'addressLoaded',
      address: address ?? {
        zipCode: text(this.zip.value),
        countyName: text(this.county.value),
        fipsCode: text(this.fips.value),
        state: text(this.stateControl?.value),
      },
    });
  }

  select(fips: string): void {
    if (this.county.disabled || !this.counties.some(county => county.countyFIPS === fips)) return;
    this.county.markAsDirty();
    this.county.markAsTouched();
    this.dispatch({ type: 'countySelected', fips });
  }
  retry(): void {
    this.dispatch({ type: 'retry' });
  }

  destroy(): void {
    this.subscriptions.unsubscribe();
    this.requests.complete();
    this.selection.complete();
    this.zip.removeValidators(this.lookupValidator);
    this.setLookupErrors(null);
  }

  private dispatch(event: CountySelectionEvent): void {
    const previous = this.state;
    const next = countySelectionReducer(previous, event);
    if (next === previous) return;
    // Cancel immediately, including when ZIP is cleared or incomplete.
    if (event.type === 'zipEdited' || event.type === 'addressLoaded' || event.type === 'retry') {
      this.requests.next(null);
    }
    if (event.type === 'addressLoaded') {
      this.writeAddress(event.address.countyName, event.address.fipsCode, event.address.state);
    } else if (event.type === 'zipEdited') {
      this.writeAddress('', '', '');
    }
    if (next.status === 'selected') {
      this.writeAddress(next.county.countyName, next.county.countyFIPS, next.county.state[this.stateFormat]);
    } else if (next.status === 'needsSelection' || next.status === 'invalidZip') {
      this.writeAddress('', '', '');
    }
    this.selection.next(next);
    this.setLookupErrors(this.lookupErrors(next.status));
    // Consumers receive one group emission containing the complete address and validation state.
    this.form.updateValueAndValidity();
    if (
      next.status === 'loading' &&
      (event.type === 'zipEdited' || event.type === 'addressLoaded' || event.type === 'retry')
    ) {
      this.requests.next({ zip: next.zip, load: event.type === 'addressLoaded' });
    }
  }

  private writeAddress(countyName: string, fipsCode: string, state: string): void {
    this.county.setValue(countyName, { emitEvent: false });
    this.fips.setValue(fipsCode, { emitEvent: false });
    this.stateControl?.setValue(state, { emitEvent: false });
  }

  private lookupErrors(status: CountySelectionState['status']): ValidationErrors | null {
    switch (status) {
      case 'incomplete':
        return { incompleteZipCode: true };
      case 'invalidZip':
        return { invalidZipCode: true };
      case 'lookupFailed':
        return { countyLookupFailed: true };
      case 'loading':
        return { countyLookupPending: true };
      case 'needsSelection':
        return { countySelectionRequired: true };
      default:
        return null;
    }
  }

  private setLookupErrors(errors: ValidationErrors | null): void {
    const otherErrors = { ...this.zip.errors };
    delete otherErrors.incompleteZipCode;
    delete otherErrors.countySelectionRequired;
    delete otherErrors.invalidZipCode;
    delete otherErrors.countyLookupFailed;
    delete otherErrors.countyLookupPending;
    const combined = { ...otherErrors, ...errors };
    this.zip.setErrors(Object.keys(combined).length ? combined : null);
  }
}
